
import { FFmpeg } from "@ffmpeg/ffmpeg";
import { fetchFile, toBlobURL } from "@ffmpeg/util";
import { getVideoMetadata } from "../lib/utils";

const MB = 1024 * 1024;
const BASE_URL = "https://unpkg.com/@ffmpeg/core@0.12.10/dist/umd";

let ffmpegInstance = null;
let loadingPromise = null;
let activeCompression = null;

const abortError = () => new DOMException("Video compression cancelled.", "AbortError");
const checkAbort = (signal) => { if (signal?.aborted) throw abortError(); };

const resetFFmpeg = (ffmpeg) => {
  if (!ffmpeg) return;
  try { ffmpeg.terminate(); } catch (error) { console.warn("FFmpeg termination failed:", error); }
  if (ffmpegInstance === ffmpeg) ffmpegInstance = null;
};

const getFFmpeg = async () => {
  if (ffmpegInstance?.loaded) return ffmpegInstance;
  if (loadingPromise) return loadingPromise;

  loadingPromise = (async () => {
    const ffmpeg = new FFmpeg();

    try {
      console.time("FFmpeg initialization");

      await ffmpeg.load({
        coreURL: await toBlobURL(`${BASE_URL}/ffmpeg-core.js`, "text/javascript"),
        wasmURL: await toBlobURL(`${BASE_URL}/ffmpeg-core.wasm`, "application/wasm"),
      });

      console.timeEnd("FFmpeg initialization");
      ffmpegInstance = ffmpeg;
      return ffmpeg;
    } catch (error) {
      resetFFmpeg(ffmpeg);
      throw error;
    }
  })();

  try {
    return await loadingPromise;
  } finally {
    loadingPromise = null;
  }
};

const getCompressionDecision = (file, metadata, options) => {
  const { minSize = 20 * MB, maxSize = 50 * MB, maxDuration = 120, minBitrate = 2500000 } = options;
  const bitrate = (file.size * 8) / metadata.duration;

  if (file.size < minSize) return { compress: false, reason: "Video is smaller than 20 MB.", bitrate };
  if (file.size > maxSize) return { compress: false, reason: "Video exceeds browser compression size limit.", bitrate };
  if (metadata.duration > maxDuration) return { compress: false, reason: "Video is too long for efficient browser compression.", bitrate };
  if (bitrate < minBitrate) return { compress: false, reason: "Video already has a relatively low bitrate.", bitrate };

  return { compress: true, reason: "Video is eligible for compression.", bitrate };
};

export const stopVideoCompression = (signal) => {
  if (activeCompression && (!signal || activeCompression.signal === signal)) {
    activeCompression.cancelled = true;
    resetFFmpeg(activeCompression.ffmpeg);
  }
};

export const compressVideo = async (file, options = {}) => {
  if (!(file instanceof File)) throw new Error("Invalid video file.");

  const { signal, crf = 28, preset = "ultrafast", maxWidth = 1280, encodingTimeout = 30000 } = options;
  checkAbort(signal);

  let metadata;

  try {
    metadata = await getVideoMetadata(file);
  } catch (error) {
    checkAbort(signal);
    console.warn("Compression skipped: metadata unavailable.", file.name, error);
    return file;
  }

  checkAbort(signal);

  const decision = getCompressionDecision(file, metadata, options);

  console.log("Alakoze video compression decision:", {
    file: file.name,
    sizeMB: +(file.size / MB).toFixed(2),
    duration: +metadata.duration.toFixed(2),
    bitrateMbps: +(decision.bitrate / 1000000).toFixed(2),
    compress: decision.compress,
    reason: decision.reason,
  });

  if (!decision.compress) return file;

  const id = Math.random().toString(36).slice(2);
  const extension = file.name.split(".").pop()?.toLowerCase() || "mp4";
  const input = `input-${id}.${extension}`;
  const output = `output-${id}.mp4`;

  let ffmpeg;
  let cancelled = false;

  const onAbort = () => {
    cancelled = true;
    if (ffmpeg && activeCompression?.signal === signal) resetFFmpeg(ffmpeg);
  };

  signal?.addEventListener("abort", onAbort, { once: true });

  try {
    checkAbort(signal);
    ffmpeg = await getFFmpeg();
    checkAbort(signal);

    activeCompression = { ffmpeg, signal, cancelled: false };

    await ffmpeg.writeFile(input, await fetchFile(file));
    checkAbort(signal);

    console.log("Starting FFmpeg compression:", file.name);

    const exitCode = await ffmpeg.exec([
      "-i", input,
      "-vf", `scale='min(${maxWidth},iw)':-2`,
      "-c:v", "libx264",
      "-preset", preset,
      "-crf", String(crf),
      "-c:a", "aac",
      "-b:a", "128k",
      "-movflags", "+faststart",
      output,
    ], encodingTimeout);

    checkAbort(signal);

    if (exitCode !== 0) throw new Error(`FFmpeg exited with code ${exitCode}.`);

    const data = await ffmpeg.readFile(output);
    checkAbort(signal);

    const blob = new Blob([data], { type: "video/mp4" });

    if (!blob.size || blob.size >= file.size) {
      console.log("Compression did not reduce video size. Using original.");
      return file;
    }

    console.log("Video compression successful:", {
      originalMB: +(file.size / MB).toFixed(2),
      compressedMB: +(blob.size / MB).toFixed(2),
      savedPercent: `${Math.round((1 - blob.size / file.size) * 100)}%`,
    });

    return new File([blob], file.name.replace(/\.[^.]+$/, "") + ".mp4", {
      type: "video/mp4",
      lastModified: Date.now(),
    });
  } catch (error) {
    if (cancelled || signal?.aborted) throw abortError();

    console.warn("Video compression failed. Using original:", file.name, error);

    if (ffmpeg && !ffmpeg.loaded) resetFFmpeg(ffmpeg);
    return file;
  } finally {
    signal?.removeEventListener("abort", onAbort);

    if (activeCompression?.ffmpeg === ffmpeg) activeCompression = null;

    if (ffmpeg?.loaded) {
      await ffmpeg.deleteFile(input).catch(() => {});
      await ffmpeg.deleteFile(output).catch(() => {});
    }
  }
};

export const compressVideos = async (files, options = {}) => {
  const { signal, onProgress } = options;
  const results = [];
  let originalBytes = 0;
  let compressedBytes = 0;

  for (let index = 0; index < files.length; index++) {
    checkAbort(signal);

    const file = files[index];
    const compressed = await compressVideo(file, options);

    checkAbort(signal);

    results.push(compressed);
    originalBytes += file.size;
    compressedBytes += compressed.size;

    onProgress?.(Math.round((index + 1) / files.length * 100));
  }

  return {
    files: results,
    originalMB: +(originalBytes / MB).toFixed(2),
    compressedMB: +(compressedBytes / MB).toFixed(2),
    savedPercent: originalBytes ? Math.round((1 - compressedBytes / originalBytes) * 100) : 0,
  };
};
